import { useState, type ReactNode } from 'react'
import { Radar, Check, AlertTriangle, Loader2, ArrowRight, ArrowLeft, RotateCcw, Code2, ChevronDown, ChevronUp } from 'lucide-react'
import { TM_SCENARIOS } from '../data'
import type { ChecklistItem, SimAlert } from '../types'
import { StageGuidedShell } from './StageGuidedShell'
import type { GuidedStep } from './StageGuidedShell'
import { TM_CHECKLIST_ICONS, TM_CHECKLIST_DESCRIPTIONS } from './tmExperienceShared'

interface TmGuidedViewProps {
  scenarioId: string
  onSelectScenario: (id: string) => void
  onRun: () => void
  running: boolean
  alerts: SimAlert[] | null
  customerName: string | null
  checklistItems: ChecklistItem[]
  checklistEnabled: Record<string, boolean>
  onToggleChecklistItem: (id: string) => void
  technicalDetails: ReactNode
}

export function TmGuidedView({
  scenarioId, onSelectScenario, onRun, running, alerts, customerName, checklistItems, checklistEnabled, onToggleChecklistItem, technicalDetails,
}: TmGuidedViewProps) {
  const [showTechnical, setShowTechnical] = useState(false)
  const name = customerName ?? 'this customer'
  const clean = alerts !== null && alerts.length === 0

  const steps: GuidedStep[] = [
    {
      label: 'Overview',
      render: ({ next }) => (
        <div className="rounded-[18px] p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,.12), rgba(167,139,250,.06))', border: '1px solid rgba(99,102,241,.25)' }}>
          <div className="w-16 h-16 rounded-[18px] flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(99,102,241,.18)' }}>
            <Radar size={30} className="text-[#4f46e5]" />
          </div>
          <h2 className="text-[22px] font-bold text-[#1e293b]">Transaction Monitoring</h2>
          <p className="text-[14px] text-[#4f46e5] mt-2 max-w-md mx-auto leading-relaxed">
            Watch {name}&apos;s ongoing transactions for structuring, unusual velocity, and large single transfers —
            flagging anything worth a closer look.
          </p>
          <button
            onClick={next}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-[10px] text-[14px] font-semibold text-white transition-all"
            style={{ background: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,.5)' }}
          >
            Get started <ArrowRight size={15} />
          </button>
        </div>
      ),
    },
    {
      label: 'What to include',
      render: ({ next, back }) => (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">What rules should run?</h3>
            <p className="text-[12px] text-[#475569] mt-1">Tap anything that isn&apos;t needed for this customer to leave it out.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {checklistItems.map((item) => {
              const Icon = TM_CHECKLIST_ICONS[item.id] ?? Radar
              const active = checklistEnabled[item.id] ?? true
              return (
                <button
                  key={item.id}
                  onClick={() => onToggleChecklistItem(item.id)}
                  className="text-left rounded-[14px] p-4 transition-all"
                  style={active
                    ? { background: '#f8fafc', border: '1px solid rgba(99,102,241,.35)' }
                    : { background: '#f8fafc', border: '1px solid #e5e7eb', opacity: 0.5 }
                  }
                >
                  <Icon size={20} className={active ? 'text-[#4f46e5]' : 'text-[#475569]'} />
                  <p className="text-[13px] font-semibold text-[#1e293b] mt-2">{item.label}</p>
                  <p className="text-[12px] text-[#475569] mt-1 leading-snug">{TM_CHECKLIST_DESCRIPTIONS[item.id] ?? ''}</p>
                </button>
              )
            })}
          </div>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={next} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white" style={{ background: '#6366f1' }}>
              Next <ArrowRight size={13} />
            </button>
          </div>
        </div>
      ),
    },
    {
      label: 'Try it',
      render: ({ next, back }) => (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">Try it yourself</h3>
            <p className="text-[12px] text-[#475569] mt-1">Pick a sample activity pattern to run through the rules.</p>
          </div>
          <div className="space-y-1.5">
            {TM_SCENARIOS.map((s) => (
              <button
                key={s.id}
                onClick={() => onSelectScenario(s.id)}
                className="w-full text-left px-3 py-2.5 rounded-[9px] transition-colors"
                style={scenarioId === s.id
                  ? { background: 'rgba(99,102,241,.14)', border: '1px solid rgba(99,102,241,.4)' }
                  : { background: '#f8fafc', border: '1px solid #e5e7eb' }
                }
              >
                <p className="text-[13px] font-semibold text-[#1e293b]">{s.label}</p>
                <p className="text-[12px] text-[#475569] mt-0.5 leading-snug">{s.description}</p>
              </button>
            ))}
          </div>
          <button
            onClick={onRun}
            disabled={running}
            className="w-full py-3 rounded-[10px] text-[14px] font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,.5)' }}
          >
            {running
              ? <span className="flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" />Streaming transactions…</span>
              : 'Watch the activity'}
          </button>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={next} disabled={alerts === null} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: '#6366f1' }}>
              See result <ArrowRight size={13} />
            </button>
          </div>
        </div>
      ),
    },
    {
      label: 'Result',
      render: ({ back, restart }) => (
        <div className="space-y-4">
          <div className="rounded-[18px] p-6 flex items-center gap-4" style={{ background: clean ? 'rgba(52,211,153,.06)' : 'rgba(248,113,113,.06)', border: `1px solid ${clean ? 'rgba(52,211,153,.25)' : 'rgba(248,113,113,.25)'}` }}>
            <div className="w-12 h-12 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: clean ? 'rgba(52,211,153,.15)' : 'rgba(248,113,113,.15)' }}>
              {clean ? <Check size={24} className="text-emerald-700" /> : <AlertTriangle size={24} className="text-red-700" />}
            </div>
            <p className="text-[15px] font-semibold text-[#1e293b]">
              {alerts === null ? 'Waiting for a result…' : clean ? `Nothing unusual — ${name}'s activity looks clean.` : `${alerts.length} alert${alerts.length !== 1 ? 's' : ''} raised for ${name}.`}
            </p>
          </div>

          <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
            <button
              onClick={() => setShowTechnical((s) => !s)}
              className="w-full flex items-center justify-between px-4 py-3 transition-colors hover:bg-[#ffffff]"
              style={{ background: '#f8fafc' }}
            >
              <span className="flex items-center gap-2 text-[12px] font-semibold text-[#475569]">
                <Code2 size={13} /> See how this works technically
              </span>
              {showTechnical ? <ChevronUp size={13} className="text-[#64748b]" /> : <ChevronDown size={13} className="text-[#64748b]" />}
            </button>
            {showTechnical && <div className="p-4 space-y-3" style={{ background: '#ffffff' }}>{technicalDetails}</div>}
          </div>

          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={restart} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <RotateCcw size={13} /> Start over
            </button>
          </div>
        </div>
      ),
    },
  ]

  return <StageGuidedShell steps={steps} />
}
